import { Button, Input, Select, Space, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import AddNewLeaveModal from "../../../../components/Settings/Leave/AddNewLeaveModal";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  useGetLeaveTypeListQuery,
  useUpdateLeaveTypeMutation,
} from "../../../../features/leaveType/leaveTypeApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../utils/number";
import {
  addKeyInArray,
  selectSearchOption,
  skipData,
} from "../../../../utils/utils";

export const leaveSettingsTabList = [
  {
    label: "Leave Type",
    link: "/admin/settings/leave",
  },
  {
    label: "Leave Prorate",
    link: "/admin/settings/leave/leave-prorate",
  },
  {
    label: "Adjustment",
    link: "/admin/settings/leave/adjustment",
  },
  {
    label: "Others",
    link: "/admin/settings/leave/others",
  },
];

type StatusType = {
  id: number;
  body: {
    title: string;
    total_days: string;
    is_half_day_enable: number;
    is_published: number;
  };
};

export default function LeaveType() {
  const { open, handleOpen, handleClose } = useModal<
    StatusType | number,
    "STATUS"
  >();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    handleSearch,
    resetPagination,
    keyword,
  } = useTable();
  const [status, setStatus] = useState(undefined);
  const {
    data: leaveTypeListData,
    isLoading: isLeaveTypeListLoading,
    isError,
    error,
  } = useGetLeaveTypeListQuery({
    params: {
      limit,
      skip: skipData(page, limit),
      is_published: status,
      ...(keyword ? { search: keyword } : {}),
    },
  });

  // console.log(leaveTypeListData);
  const [
    updateLeaveType,
    {
      isSuccess: isUpdateSuccess,
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateLeaveTypeMutation();
  useError(isError, error, "Failed fetching leave type");
  useError(
    isUpdateError,
    updateError,
    `Leave ${
      typeof open.data !== "number" && open?.data?.body?.is_published
        ? "publish"
        : "unpublish"
    } fail`
  );

  const columns: ColumnsType<any> = [
    {
      title: "#SL",
      dataIndex: "index",
      align: "center",
      width: 50,
      render: (_: any, __: any, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Leave Name",
      dataIndex: "title",
    },
    {
      title: "Total Days",
      dataIndex: "total_days",
      align: "center",
    },
    {
      title: "Half Day",
      dataIndex: "is_half_day_enable",
      align: "center",
      render: (value) => (
        <span className={value ? "status-success" : "status-danger"}>
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_published",
      render: (value) => (
        <span className={`${value ? "status-success" : "status-dark"}`}>
          {value ? "Published" : "Unpublished"}
        </span>
      ),
    },
    {
      title: "Action",
      align: "center",
      render: (val) => (
        <Space size={8}>
          <Switch
            className="custom-switch"
            checked={val?.is_published === 1}
            disabled={isUpdateLoading}
            onChange={(checked) => {
              handleOpen({
                type: "STATUS",
                data: {
                  id: val?.id,
                  body: {
                    title: val?.title,
                    total_days: val?.total_days,
                    is_half_day_enable: val?.is_half_day_enable,
                    is_published: checked ? 1 : 0,
                  },
                },
              });
            }}
          />
          <Button
            icon={<EditIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "UPDATE",
                data: val?.id,
              })
            }
          />
          {/* <Button
            icon={<DeleteIcon />}
            size="small"
            className="action-button"
            danger
          /> */}
        </Space>
      ),
    },
  ];

  const handleStatus = () => {
    if (typeof open.data !== "number" && open?.data?.id) {
      updateLeaveType({ id: open.data?.id, data: open.data?.body });
    }
  };

  useEffect(() => {
    if (isUpdateSuccess) {
      toast(
        "success",
        `Leave successfully ${
          typeof open.data !== "number" && open?.data?.body?.is_published
            ? "published"
            : "unpublished"
        }`
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess]);

  return (
    <SidebarLayout title="Leave Settings" tabList={leaveSettingsTabList}>
      <CustomTable
        loading={isLeaveTypeListLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={leaveTypeListData?.data?.total_leave_types}
        dataSource={addKeyInArray(leaveTypeListData?.data?.leave_types)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              onChange={handleSearch}
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={statusList}
              className="min-w-[120px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => {
                setStatus(val);
                resetPagination();
              }}
            />

            <Button
              className="middle-button"
              type="primary"
              icon={<AddIcon />}
              onClick={() =>
                handleOpen({
                  type: "ADD",
                })
              }
            >
              Add New Leave
            </Button>
          </>
        }
      />

      {(open.type === "ADD" || open.type === "UPDATE") &&
        (typeof open?.data === "number" ||
          typeof open?.data === "undefined") && (
          <AddNewLeaveModal
            open={open.open}
            type={open.type}
            data={open.data}
            handleClose={handleClose}
          />
        )}

      {open?.type === "STATUS" && (
        <ConfirmModal
          open={open.open}
          handleClose={handleClose}
          handleOk={handleStatus}
          confirmTitle="Wait!"
          questionText={`Are you sure you want to ${
            typeof open.data !== "number" && open?.data?.body?.is_published
              ? "publish"
              : "unpublish"
          } this leave ?`}
          okText="Yes"
          cancelText="No"
          okLoading={isUpdateLoading}
        />
      )}
    </SidebarLayout>
  );
}

const statusList = [
  { label: "Published", value: 1 },
  { label: "Unpublished", value: 0 },
];
